import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, Link as LinkIcon, LogOut, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { getTraktAuthUrl, syncWithTrakt } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export const SettingsPage = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [traktConnected, setTraktConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    // Check if Trakt is connected
    if (user?.trakt_access_token) {
      setTraktConnected(true);
    }

    // Handle Trakt callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      // Handle Trakt OAuth callback would go here
      // For now, just clear the URL params
      window.history.replaceState({}, document.title, '/settings');
    }
  }, [user]);

  const handleConnectTrakt = async () => {
    try {
      const redirectUri = `${window.location.origin}/settings`;
      const { auth_url } = await getTraktAuthUrl(redirectUri);
      window.location.href = auth_url;
    } catch (error) {
      toast.error('Kon Trakt niet verbinden');
    }
  };

  const handleSyncTrakt = async () => {
    setSyncing(true);
    try {
      await syncWithTrakt();
      toast.success('Gesynchroniseerd met Trakt');
    } catch (error) {
      toast.error('Synchronisatie mislukt');
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Uitgelogd');
  };

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  return (
    <div data-testid="settings-page" className="min-h-screen p-6 md:p-12">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 rounded-xl bg-indigo-600/20">
            <SettingsIcon className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-display font-bold">Instellingen</h1>
            <p className="text-gray-400">{user?.email}</p>
          </div>
        </div>

        {/* Account Section */}
        <section className="glass-card p-6 mb-6">
          <h2 className="text-lg font-display font-semibold mb-4">Account</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">E-mail</p>
                <p className="text-sm text-gray-400">{user?.email}</p>
              </div>
            </div>

            {user?.name && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Naam</p>
                  <p className="text-sm text-gray-400">{user.name}</p>
                </div>
              </div>
            )}

            <Separator className="bg-white/10" />

            <Button
              variant="destructive"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Uitloggen
            </Button>
          </div>
        </section>

        {/* Trakt Integration */}
        <section className="glass-card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <LinkIcon className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-display font-semibold">Trakt.tv Integratie</h2>
          </div>
          
          <p className="text-sm text-gray-400 mb-4">
            Synchroniseer je kijkgeschiedenis met Trakt.tv om je gegevens over meerdere apparaten te delen.
          </p>

          {traktConnected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-400">
                <Check className="w-4 h-4" />
                <span className="text-sm">Verbonden met Trakt</span>
              </div>
              
              <Button
                onClick={handleSyncTrakt}
                disabled={syncing}
                data-testid="sync-trakt-btn"
                className="w-full bg-red-600 hover:bg-red-700"
              >
                {syncing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Synchroniseren...
                  </>
                ) : (
                  'Nu Synchroniseren'
                )}
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleConnectTrakt}
              data-testid="connect-trakt-btn"
              className="w-full bg-red-600 hover:bg-red-700"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Verbind met Trakt
            </Button>
          )}
        </section>

        {/* Preferences */}
        <section className="glass-card p-6">
          <h2 className="text-lg font-display font-semibold mb-4">Voorkeuren</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="notifications" className="font-medium">Notificaties</Label>
                <p className="text-sm text-gray-400">Ontvang meldingen voor nieuwe afleveringen</p>
              </div>
              <Switch id="notifications" data-testid="notifications-switch" />
            </div>

            <Separator className="bg-white/10" />

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="auto-mark" className="font-medium">Auto-markeren</Label>
                <p className="text-sm text-gray-400">Markeer afleveringen automatisch als gekeken na afloop</p>
              </div>
              <Switch id="auto-mark" data-testid="auto-mark-switch" />
            </div>

            <Separator className="bg-white/10" />

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="spoilers" className="font-medium">Verberg Spoilers</Label>
                <p className="text-sm text-gray-400">Verberg afleveringstitels tot je ze hebt gezien</p>
              </div>
              <Switch id="spoilers" defaultChecked data-testid="spoilers-switch" />
            </div>
          </div>
        </section>

        {/* App Info */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Episo v1.0.0</p>
          <p className="mt-1">Data powered by TMDB & TheTVDB</p>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
